/**
 * The back office's composition root.
 *
 * Everything with a rule in it lives elsewhere and is tested there. This file
 * decides which implementations get wired together, and nothing else — the same
 * discipline the till keeps, and the reason `main.ts` is the one file in either
 * app with no unit test of its own.
 *
 * It is a CLIENT OF THE PUBLIC API and holds no database connection, no
 * credential beyond its bearer token, and no server-only import. That is D12
 * rather than architecture taste: if the console ever needed a private back
 * door, the public surface would be the wrong shape and a partner would find
 * out before we did.
 */

import { createApp, h, reactive, watch } from "vue";
import { cssVariables, type DensityName, type ThemeName } from "@gf/design";
// The typeface and the shared layer. See the note in `apps/pos/src/main.ts`.
import "@gf/ui/fonts.css";
import "@gf/ui/base.css";
// The office vocabulary (panels, tables, buttons, chips, drawers) — this app's
// alone; see the header of `@gf/ui/office.css` for why it is not in base.css.
import "@gf/ui/office.css";
import { ConsoleApi, type TableCards } from "./api";
import { venueToday } from "./format";
import { officeVenue } from "./venue-link";
import {
  loadChecks,
  loadCheck,
  loadFiscal,
  loadKitchen,
  loadSales,
  loadShifts,
  loading,
  customRange,
  rangeOf,
  type Loaded,
  type RangeName,
} from "./load";
import { CATALOGS, negotiate, type LocaleName, type MessageKey } from "./locales";
import { orderFromHash, screenAvailable, screenFromHash, type ScreenId } from "./nav";
import ConsoleApp from "./ui/ConsoleApp.vue";
import SignInScreen from "./ui/SignInScreen.vue";
import {
  restore,
  signIn as postSignIn,
  signOut as postSignOut,
  type Session,
  type SignInFailure,
} from "./session";

// --- Theme -----------------------------------------------------------------

function preferredTheme(): ThemeName {
  if (window.matchMedia("(prefers-contrast: more)").matches) return "highContrast";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(theme: ThemeName, density: DensityName): void {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  document.querySelector("style[data-gf-tokens]")?.remove();
  const style = document.createElement("style");
  style.setAttribute("data-gf-tokens", "");
  // `color-scheme` with the tokens: without it a dark office drew the browser's
  // LIGHT date pickers, selects and checkboxes on a near-black page.
  const scheme = theme === "dark" ? "dark" : "light";
  style.textContent = `:root {\n${cssVariables(theme, density, reduced)}\ncolor-scheme: ${scheme};\n}`;
  document.head.appendChild(style);
}

/**
 * "comfortable" — a manager's laptop, at arm's length, with a mouse.
 *
 * The density has been defined in the design system since it was written and
 * had no consumer until now. 40 px targets rather than the till's 56: nothing
 * on this screen is tapped by somebody holding a tray.
 */
applyTheme(preferredTheme(), "comfortable" satisfies DensityName);
for (const query of ["(prefers-color-scheme: dark)", "(prefers-contrast: more)"]) {
  window.matchMedia(query).addEventListener("change", () => {
    applyTheme(preferredTheme(), "comfortable");
  });
}

// --- Configuration ---------------------------------------------------------

/**
 * NO BEARER TOKEN HERE ANY MORE.
 *
 * This used to hold one in a build-time variable, exactly as the till does,
 * with a comment saying it was fine for a demo and not for a customer. It now
 * comes from a sign-in: a manager's own PIN, checked server-side against the
 * same roster the till uses, exchanged for a session that expires and can be
 * revoked.
 *
 * `VITE_GF_API_KEY` is gone rather than kept as a fallback. A fallback
 * credential is one that works, which means it is one somebody ships.
 */
const venue = officeVenue({
  search: window.location.search,
  storage: (() => {
    try {
      return window.localStorage;
    } catch {
      return null;
    }
  })(),
  fallback: import.meta.env["VITE_GF_VENUE"] ?? "33333333-3333-4333-8333-333333333333",
});
// The id has done its job once remembered; the address bar goes back to plain.
if (venue.fromLink) window.history.replaceState(null, "", window.location.pathname + window.location.hash);

/** The "Připravujeme" drawings: only a demo build shows them. */
const previews = import.meta.env["VITE_GF_PREVIEWS"] === "1";

const config = {
  baseUrl: import.meta.env["VITE_GF_API"] ?? "http://localhost:3000",
  venueId: venue.venueId,
} as const;

/**
 * Built PER SESSION, because the token is what it is built around.
 *
 * Null until somebody signs in, which is also what the render function keys
 * off: there is no client to make a request with, so there is no way for a
 * screen to quietly render against a dead credential.
 */
let api: ConsoleApi | null = null;

// --- i18n ------------------------------------------------------------------

/**
 * Whatever was chosen last, then the DEPLOYMENT's language, then the browser's.
 *
 * `VITE_GF_LOCALE` exists because the browser is the wrong authority here. A
 * Czech venue's back office should open in Czech on a laptop that happens to be
 * set to English — which is most demo laptops, and quite a few real ones. The
 * browser preference still wins over nothing at all, and the switch in Settings
 * always wins over both.
 */
const stored = window.localStorage.getItem("gf.console.locale");
const deployed = import.meta.env["VITE_GF_LOCALE"];
const initialLocale: LocaleName =
  stored && stored in CATALOGS
    ? (stored as LocaleName)
    : typeof deployed === "string" && deployed in CATALOGS
      ? (deployed as LocaleName)
      : negotiate([...navigator.languages]);

const state = reactive({
  screen: screenFromHash(window.location.hash),
  orderId: orderFromHash(window.location.hash),
  range: "week" as RangeName,
  /** The period chosen by hand; used when `range` is "custom". */
  custom: null as { from: string; to: string } | null,
  locale: initialLocale,

  /** What the venue believes about itself. Everything below dates from it. */
  venue: null as {
    name: string;
    timezone: string;
    businessDayStartMinutes: number;
    fiscalRegistrationId: string | null;
  } | null,
  devices: [] as { deviceId: string; name: string; fiscalTillId: string | null }[],
  staff: [] as { staffId: string; displayName: string; roleId: string }[],
  /** The modules in effect at the venue; null until it has answered (everything shows meanwhile). */
  modules: null as readonly string[] | null,

  sales: loading() as Loaded<Awaited<ReturnType<ConsoleApi["sales"]>>>,
  checks: loading() as Loaded<Awaited<ReturnType<ConsoleApi["checks"]>>>,
  detail: null as Loaded<Awaited<ReturnType<ConsoleApi["check"]>>> | null,
  shifts: loading() as Loaded<Awaited<ReturnType<ConsoleApi["shifts"]>>>,
  kitchen: loading() as Loaded<Awaited<ReturnType<ConsoleApi["kitchenTimings"]>>>,
  fiscal: loading() as Loaded<Awaited<ReturnType<ConsoleApi["fiscal"]>>>,
  gaps: [] as Record<string, unknown>[],
  issued: 0,

  /**
   * The cards last printed. NOT FETCHED ON ARRIVAL.
   *
   * Every other screen loads when you open it; this one does nothing until a
   * manager types table names and presses the button. Printing forty cards
   * RETIRES the previous forty — every mint is fresh, because a laminated code
   * cannot be recalled — so a screen that minted on navigation would silently
   * invalidate a venue's cards every time somebody clicked the wrong nav item.
   */
  tableCards: null as TableCards | null,
  tableCardsBusy: false,
  tableCardsNotice: null as string | null,

  // --- Who is looking at this ---------------------------------------------
  session: null as Session | null,
  signingIn: false,
  signInFailure: null as SignInFailure | null,
});

/**
 * A missing key renders as the key itself, never as an empty string.
 *
 * An empty label is invisible and ships; a visible `sales.revenue` on screen is
 * found in about four seconds. The locale parity gate makes it unreachable in
 * CI, and this is what happens if it ever is not.
 */
const t = (key: string): string => {
  const catalogue = CATALOGS[state.locale].messages as Record<string, string>;
  return catalogue[key] ?? (CATALOGS.cs.messages as Record<string, string>)[key] ?? key;
};

// --- Dates -----------------------------------------------------------------

/**
 * TODAY IS THE VENUE'S TRADING DAY, not the browser's calendar day.
 *
 * At 01:30 on Saturday a Vinohrady pub is still trading Friday, and a back
 * office that said otherwise would show an owner an empty Saturday while their
 * bar was full. Derived from the venue's own clock, which is why nothing loads
 * until the venue has answered.
 */
const today = (): string => {
  const venue = state.venue;
  if (!venue) return new Date().toISOString().slice(0, 10);
  return venueToday(new Date(), venue.timezone, venue.businessDayStartMinutes);
};

const currentRange = () => rangeOf(state.range, today(), state.custom ?? undefined);

// --- Loading ---------------------------------------------------------------

async function loadVenue(api: ConsoleApi): Promise<void> {
  try {
    const [venues, devices, staff] = await Promise.all([
      api.venues(),
      api.devices(),
      api.staff(),
    ]);
    const mine = venues.venues.find((v) => v.venueId === config.venueId) ?? venues.venues[0];
    state.venue = mine
      ? {
          name: mine.name,
          timezone: mine.timezone,
          businessDayStartMinutes: mine.businessDayStartMinutes,
          fiscalRegistrationId: mine.fiscalRegistrationId,
        }
      : null;
    state.devices = devices.devices;
    state.staff = staff.staff;
    state.modules = (await api.modules()).effective;
  } catch (error) {
    // Not fatal: every screen below carries its own failure, and a venue we
    // could not name still has sales we can show.
    console.warn("console: could not read the venue —", (error as Error).message);
  }
}

/**
 * PASSED THE CLIENT, rather than reaching for a module-level one.
 *
 * `api` is null until somebody signs in, so a `refresh()` that closed over it
 * would be one `!` away from running against nothing — and the interval below
 * fires every minute whether or not anybody is signed in. Taking it as an
 * argument makes "there is no client" a thing the caller has to answer for.
 */
async function refresh(api: ConsoleApi): Promise<void> {
  /**
   * TODAY'S SCREEN IS ALWAYS TODAY, whatever the period picker says.
   *
   * It had no picker of its own and read whichever range happened to be
   * selected, so a tile labelled "Takings today" showed the week's 180 644 Kč
   * across 281 receipts — a number that is not wrong so much as answering a
   * different question, which is worse, because nothing on the screen said so.
   */
  const range = state.screen === "today" ? rangeOf("today", today()) : currentRange();
  const period = today().slice(0, 4);

  // The menu is not here: its screen loads and writes for itself.
  const [sales, checks, shifts, kitchen, fiscal] = await Promise.all([
    loadSales(api, range),
    loadChecks(api, range),
    loadShifts(api, range),
    // A venue without the kitchen display has no kitchen timings to ask for.
    state.modules && !state.modules.includes("kitchen-display") ? Promise.resolve(state.kitchen) : loadKitchen(api, range),
    loadFiscal(api),
  ]);

  state.sales = sales;
  state.checks = checks;
  state.shifts = shifts;
  state.kitchen = kitchen;
  state.fiscal = fiscal;

  try {
    const [gaps, register] = await Promise.all([
      api.numberingGaps(period),
      api.numberingRegister(period),
    ]);
    state.gaps = gaps.gaps;
    state.issued = register.assignments.length;
  } catch (error) {
    console.warn("console: numbering unavailable —", (error as Error).message);
  }
}

/**
 * The day's takings, on the day.
 *
 * Re-read on a slow cadence rather than pushed: a back office is not a kitchen
 * screen and a minute-old figure has never cost anybody anything, while a
 * WebSocket would be a second delivery path to keep correct for no gain.
 */
window.setInterval(() => {
  // Nothing to refresh, and nothing to refresh it with, before a sign-in.
  if (api) void refresh(api);
}, 60_000);

// --- Routing ---------------------------------------------------------------

function syncFromHash(): void {
  state.screen = screenFromHash(window.location.hash);
  state.orderId = orderFromHash(window.location.hash);
}

window.addEventListener("hashchange", () => {
  syncFromHash();
  if (api) void refresh(api);
});

watch(
  () => state.orderId,
  async (orderId) => {
    // `immediate: true`, so this runs at startup — before anybody has signed
    // in, when there is no client to ask.
    if (orderId === null || api === null) {
      state.detail = null;
      return;
    }
    state.detail = loading();
    state.detail = await loadCheck(api, orderId);
  },
  { immediate: true },
);

// --- Mount -----------------------------------------------------------------

/**
 * MOUNTED THROUGH A RENDER FUNCTION, never with getters on the root props.
 *
 * Vue evaluates root props ONCE, eagerly, outside any render effect. Handing it
 * an object of getters over reactive state therefore snapshots them at mount
 * and never looks again — the screen shows whatever was true before the first
 * request returned, forever. On this app that was a permanent "Loading…"; on
 * the till the same shape ALSO hit a temporal dead zone and rendered nothing at
 * all.
 *
 * `test/reactivity-probe.spec.ts` pins the behaviour, because it is the kind of
 * framework detail that is obvious once seen and invisible in review.
 */
const appProps = () => ({
  // A deep link to a drawing, in a venue's own office, lands on Dnes.
  // …and so does a deep link to a screen for a module the venue does not have.
  screen:
    (previews || !state.screen.startsWith("preview:")) && screenAvailable(state.screen, state.modules ?? undefined)
      ? state.screen
      : ("today" as ScreenId),
  modules: state.modules ?? undefined,
  range: state.range,
  previews,
  customRange: currentRange(),
  today: today(),
  locale: state.locale,
  t,
  venueName: state.venue?.name ?? "—",
  api,
  tableCards: state.tableCards,
  tableCardsBusy: state.tableCardsBusy,
  tableCardsNotice: state.tableCardsNotice,
  signedInAs: state.session
    ? { displayName: state.session.staff.displayName, roleId: state.session.staff.roleId }
    : null,
  /**
   * The VENUE's zone, falling back to the reader's own — never to a literal.
   *
   * A hardcoded "Europe/Prague" would be a venue this console cannot serve, and
   * the boundary lint is right to refuse it. The fallback only covers the few
   * hundred milliseconds before the venue directory answers, and showing a
   * manager their own clock for that moment is honest in a way that showing
   * them Prague's would not be.
   */
  timezone: state.venue?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  venue: state.venue,
  venueId: state.session?.venueId ?? "",
  devices: state.devices,
  staff: state.staff,
  sales: state.sales,
  checks: state.checks,
  detail: state.detail,
  shifts: state.shifts,
  kitchen: state.kitchen,
  fiscal: state.fiscal,
  gaps: state.gaps,
  issued: state.issued,

  onNavigate: (screen: ScreenId) => {
    window.location.hash = `#/${screen}`;
  },
  onModulesChanged: (effective: readonly string[]) => {
    state.modules = effective;
  },
  onRange: (value: RangeName) => {
    // Custom starts from the period on screen, not from nothing.
    if (value === "custom" && !state.custom) state.custom = currentRange();
    state.range = value;
    if (api) void refresh(api);
  },
  onCustomRange: (value: { from: string; to: string }) => {
    state.custom = customRange(value.from, value.to, today());
    state.range = "custom";
    if (api) void refresh(api);
  },
  onLocale: (value: LocaleName) => {
    state.locale = value;
    window.localStorage.setItem("gf.console.locale", value);
    document.documentElement.lang = value;
  },
  onOpenCheck: (orderId: string) => {
    window.location.hash = `#/checks?order=${orderId}`;
  },
  onCloseCheck: () => {
    window.location.hash = "#/checks";
  },
  onRetry: () => {
    state.sales = loading();
    state.checks = loading();
    state.shifts = loading();
    state.kitchen = loading();
    state.fiscal = loading();
    if (api) void refresh(api);
  },
  onSignOut: () => void endSession(),

  onPrintTableCards: (tables: readonly string[]) => {
    void (async () => {
      const client = api;
      if (!client) return;
      state.tableCardsBusy = true;
      state.tableCardsNotice = null;
      try {
        state.tableCards = await client.tableCards(tables);
      } catch (error) {
        // The server names WHICH table it refused — a name too long to print,
        // usually. Shown rather than swallowed: a manager with forty tables
        // needs to know which one to rename.
        state.tableCardsNotice = error instanceof Error ? error.message : t("common.failed");
      } finally {
        state.tableCardsBusy = false;
      }
    })();
  },

});


document.documentElement.lang = state.locale;

// --- The session ------------------------------------------------------------

/**
 * Everything that happens once somebody is in.
 *
 * Named rather than inlined into the sign-in handler because a restored session
 * has to take exactly the same path as a fresh one — and the version where it
 * did not was a console that worked when you signed in and showed empty panels
 * after a reload.
 */
function beginSession(session: Session): void {
  state.session = session;
  state.signInFailure = null;
  api = new ConsoleApi({
    baseUrl: config.baseUrl,
    apiKey: session.token,
    venueId: session.venueId,
  });
  // The venue first: every date below is derived from its clock.
  const client = api;
  void loadVenue(client).then(() => refresh(client));
}

async function endSession(): Promise<void> {
  const session = state.session;
  state.session = null;
  api = null;
  // Back to a clean slate, so signing in as somebody else cannot show the
  // previous person's figures for the moment before the first fetch lands.
  state.venue = null;
  state.sales = loading();
  state.checks = loading();
  state.shifts = loading();
  state.kitchen = loading();
  state.fiscal = loading();
  state.gaps = [];
  state.issued = 0;

  if (session) await postSignOut({ baseUrl: config.baseUrl, session });
}

async function attemptSignIn(staffId: string, pin: string): Promise<void> {
  state.signingIn = true;
  state.signInFailure = null;
  try {
    const result = await postSignIn({
      baseUrl: config.baseUrl,
      venueId: config.venueId,
      staffId,
      pin,
    });
    if (result.ok) beginSession(result.session);
    else state.signInFailure = result;
  } finally {
    // In a `finally`, so a throw cannot leave the button spinning forever with
    // no way back to the form.
    state.signingIn = false;
  }
}

/**
 * A session that survived a reload, if there is one.
 *
 * Checked for expiry inside `restore`, so a dead token draws the sign-in screen
 * rather than a dashboard whose every panel answers 401.
 */
const restored = restore();
if (restored) beginSession(restored);

/** Unused at runtime; keeps the message key type reachable from the app entry. */
export type { MessageKey };

// --- Mount, LAST ------------------------------------------------------------

// Mounting renders synchronously, so everything the first render touches has to
// exist already. See the till's main.ts for the load-bearing version of this
// comment: mounting mid-module threw in the temporal dead zone and left a blank
// screen that no test in the repository could see.
const vueApp = createApp({
  /**
   * WHICH SCREEN, decided on every render.
   *
   * `state.session` is reactive and this is a render function, so signing in
   * and signing out swap the whole app with no router and no reload. A `v-if`
   * inside `ConsoleApp` would have meant mounting the back office's entire
   * component tree behind a sign-in form — every one of which would have
   * started fetching against a client that is null.
   */
  render: () =>
    state.session === null
      ? h(SignInScreen, {
          t,
          venueName: null,
          busy: state.signingIn,
          failure: state.signInFailure,
          onSubmit: (staffId: string, pin: string) => void attemptSignIn(staffId, pin),
        })
      : h(ConsoleApp, appProps()),
});
// Vue keeps a render error to itself; handed on, the error report hears it.
vueApp.config.errorHandler = (error) => {
  console.error(error);
  dispatchEvent(new ErrorEvent("error", { error }));
};
vueApp.mount("#app");
// What breaks in the office reaches the server's log, once somebody is signed in.
void import("@gf/sync-client/error-report").then(({ reportErrors, postReports }) =>
  reportErrors({
    target: window,
    where: () => location.pathname + location.hash,
    send: postReports({ baseUrl: config.baseUrl, app: "console", apiKey: () => state.session?.token ?? null }),
  }),
);
